import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

import { type PreviewVariant } from "../components/PreviewFeature/constants";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { isStaffPreviewingFlag } from "../hooks/useFeatureFlags";
import { useTenant } from "../hooks/useTenant";

type PreviewFeatureContextValue = {
  isPreview: boolean;
  activeFlag: FeatureFlagKey | null;
  /** Whether the active scope keeps an existing experience available behind the preview. */
  activeVariant: PreviewVariant;
};

const PreviewFeatureContext = createContext<PreviewFeatureContextValue>({
  isPreview: false,
  activeFlag: null,
  activeVariant: "gated",
});

type PreviewScopeEntry = {
  flags: readonly FeatureFlagKey[];
  variant: PreviewVariant;
};

/**
 * Each mounted {@link PreviewFeatureScope} registers its flags under its own id
 * and unregisters on unmount, so concurrently-mounted scopes (e.g. a layout
 * route wrapping an Outlet plus a nested route) don't clobber one another.
 */
type PreviewFeatureScopeRegistry = {
  registerScope: (id: string, entry: PreviewScopeEntry) => void;
  unregisterScope: (id: string) => void;
};

const PreviewFeatureScopeRegistryContext = createContext<PreviewFeatureScopeRegistry | null>(null);

export function usePreviewFeature(): PreviewFeatureContextValue {
  return useContext(PreviewFeatureContext);
}

/**
 * Mount once in App.tsx. Route-level preview is declared with {@link PreviewFeatureScope}
 * in main.tsx; left-nav preview uses `previewFlag` on nav items instead.
 *
 * Checklist for a new in-development surface:
 * 1. Confirm the flag has `inDevelopment: true` in feature flag metadata.
 * 2. Route-level pages: wrap in `PreviewFeatureScope` for breadcrumb preview.
 * 3. Embedded sections/tiles: place `StaffInDevelopmentIcon` next to the title.
 * 4. Nav entries: matching `previewFlag` on the nav item (auto via flag gating helpers).
 */
export function PreviewFeatureProvider({ children }: PropsWithChildren) {
  const [scopesById, setScopesById] = useState<ReadonlyMap<string, PreviewScopeEntry>>(
    () => new Map()
  );
  const { isStaff } = useCurrentUser();
  const { tenant } = useTenant();

  const registerScope = useCallback((id: string, entry: PreviewScopeEntry) => {
    setScopesById((prev) => {
      const next = new Map(prev);
      next.set(id, entry);
      return next;
    });
  }, []);

  const unregisterScope = useCallback((id: string) => {
    setScopesById((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const registry = useMemo<PreviewFeatureScopeRegistry>(
    () => ({ registerScope, unregisterScope }),
    [registerScope, unregisterScope]
  );

  const activePreview = useMemo(() => {
    for (const { flags, variant } of scopesById.values()) {
      const match = flags.find((flag) =>
        isStaffPreviewingFlag(flag, tenant?.featureFlags, isStaff)
      );
      if (match) {
        return { flag: match, variant };
      }
    }
    return null;
  }, [scopesById, isStaff, tenant?.featureFlags]);

  const value = useMemo(
    () => ({
      isPreview: activePreview !== null,
      activeFlag: activePreview?.flag ?? null,
      activeVariant: activePreview?.variant ?? "gated",
    }),
    [activePreview]
  );

  return (
    <PreviewFeatureScopeRegistryContext.Provider value={registry}>
      <PreviewFeatureContext.Provider value={value}>{children}</PreviewFeatureContext.Provider>
    </PreviewFeatureScopeRegistryContext.Provider>
  );
}

/**
 * Declares which feature flag scopes preview for the current route or UI subtree.
 * Breadcrumb preview chrome reads this via {@link usePreviewFeature}.
 */
type PreviewFeatureScopeProps = PropsWithChildren<
  ({ flag: FeatureFlagKey; flags?: never } | { flag?: never; flags: readonly FeatureFlagKey[] }) & {
    /** `newVersion` when the route keeps an existing experience available behind the preview. */
    variant?: PreviewVariant;
  }
>;

export function PreviewFeatureScope({
  flag,
  flags,
  variant = "gated",
  children,
}: PreviewFeatureScopeProps) {
  const registry = useContext(PreviewFeatureScopeRegistryContext);

  // Memoize by flag *contents* rather than array identity, so inline `flags={[...]}`
  // props don't re-run the register effect (or loop) on every parent render.
  const scopeKey = flags ? flags.join("|") : (flag ?? "");
  const scopeFlags = useMemo(
    () => (flags ? [...flags] : flag ? [flag] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopeKey]
  );

  if (registry === null) {
    throw new Error("PreviewFeatureScope must be used within PreviewFeatureProvider");
  }

  if (scopeFlags.length === 0) {
    throw new Error("PreviewFeatureScope requires `flag` or a non-empty `flags` array");
  }

  return (
    <PreviewFeatureScopeInner scopeFlags={scopeFlags} variant={variant} registry={registry}>
      {children}
    </PreviewFeatureScopeInner>
  );
}

function PreviewFeatureScopeInner({
  scopeFlags,
  variant,
  registry,
  children,
}: PropsWithChildren<{
  scopeFlags: readonly FeatureFlagKey[];
  variant: PreviewVariant;
  registry: PreviewFeatureScopeRegistry;
}>) {
  const id = useId();

  useEffect(() => {
    registry.registerScope(id, { flags: scopeFlags, variant });
    return () => {
      registry.unregisterScope(id);
    };
  }, [id, scopeFlags, variant, registry]);

  return children;
}
